import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";
import {
  TrendingUp, TrendingDown, AlertTriangle, MessageSquare, Users, RefreshCcw, X, Fingerprint, FileDown,
} from "lucide-react";

const fmtMoney = (v) =>
  v == null ? "—" : `UGX ${Math.round(Number(v)).toLocaleString()}`;

function VarianceBadge({ pct }) {
  if (pct == null) return <Badge tone="slate">no target</Badge>;
  const up = pct >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
        up ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
      }`}
    >
      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      {up ? "+" : ""}{pct}%
    </span>
  );
}

function KpiCard({ label, value, sub, icon: Icon, tone = "emerald", testid }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
    red: "bg-red-50 text-red-800 border-red-100",
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    slate: "bg-slate-50 text-slate-800 border-slate-100",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold opacity-70">{label}</div>
        {Icon && <Icon size={15} className="opacity-60" />}
      </div>
      <div className="text-xl font-bold mt-1 font-display">{value}</div>
      {sub && <div className="text-[11px] mt-0.5 opacity-70">{sub}</div>}
    </div>
  );
}

export default function ExecutiveRollup() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range.from) params.append("from_date", range.from);
      if (range.to) params.append("to_date", range.to);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data: r } = await api.get(`/executive-rollup${qs}`);
      setData(r);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const [exporting, setExporting] = useState(false);
  const exportPdf = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (range.from) params.append("from_date", range.from);
      if (range.to) params.append("to_date", range.to);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await api.get(`/executive-rollup/export.pdf${qs}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `executive-rollup_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Board pack PDF downloaded");
    } catch (e) { toast.error("PDF export failed"); }
    finally { setExporting(false); }
  };

  const setPreset = (days) => {
    const to = new Date().toISOString().slice(0, 10);
    const d = new Date(); d.setDate(d.getDate() - days);
    setRange({ from: d.toISOString().slice(0, 10), to });
    setRefreshKey((k) => k + 1);
  };

  const totals = data?.totals || {};
  const branches = data?.branches || [];
  const chartData = branches.map((b) => ({
    name: b.branch.replace("Abrah ", "").replace(" Abrah", ""),
    Target: b.sales_target,
    Actual: b.sales_actual,
  }));
  const hasSales = branches.some((b) => b.sales_target || b.sales_actual);

  return (
    <div data-testid="executive-rollup-page">
      <PageHeader
        eyebrow="Overview"
        title="Executive Rollup"
        subtitle="Cross-branch comparison — sales vs targets (from periodic reports), issues, customer feedback, attendance and staffing."
        actions={
          <div className="flex gap-2">
            <Button size="sm" onClick={exportPdf} disabled={exporting || loading}
              className="bg-emerald-900 hover:bg-emerald-800" data-testid="rollup-export-pdf">
              <FileDown size={14} className="mr-1.5" /> {exporting ? "Exporting…" : "Export PDF"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)}
              data-testid="rollup-refresh">
              <RefreshCcw size={14} className="mr-1.5" /> Refresh
            </Button>
          </div>
        }
      />

      {/* Date range */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 flex flex-wrap items-end gap-3"
        data-testid="rollup-range-bar">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">From</div>
          <Input type="date" value={range.from} data-testid="rollup-from-date"
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="h-9 w-40" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">To</div>
          <Input type="date" value={range.to} data-testid="rollup-to-date"
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="h-9 w-40" />
        </div>
        <Button size="sm" onClick={() => setRefreshKey((k) => k + 1)} data-testid="rollup-apply-range"
          className="bg-emerald-900 hover:bg-emerald-800">Apply</Button>
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setPreset(d)} data-testid={`rollup-preset-${d}d`}
            className="px-3 h-9 rounded-full border border-gray-300 text-xs text-gray-600 hover:border-emerald-700 hover:text-emerald-800 transition-colors">
            Last {d}d
          </button>
        ))}
        {(range.from || range.to) ? (
          <button onClick={() => { setRange({ from: "", to: "" }); setRefreshKey((k) => k + 1); }}
            data-testid="rollup-clear-range"
            className="px-3 h-9 rounded-full border border-gray-300 text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1">
            <X size={12} /> Clear
          </button>
        ) : (
          <Badge tone="slate">All time (default)</Badge>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-16 text-center">Loading rollup…</div>
      ) : (
        <>
          {/* Company-wide KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <KpiCard label="Sales actual" value={fmtMoney(totals.sales_actual)}
              sub={<span>vs target {fmtMoney(totals.sales_target)} <VarianceBadge pct={totals.sales_variance_pct} /></span>}
              icon={TrendingUp} tone="emerald" testid="rollup-kpi-sales" />
            <KpiCard label="Open issues" value={totals.issues_open ?? 0}
              sub={`${totals.issues_critical ?? 0} critical`} icon={AlertTriangle}
              tone={(totals.issues_critical || 0) > 0 ? "red" : "slate"} testid="rollup-kpi-issues" />
            <KpiCard label="Feedback" value={totals.feedback_total ?? 0}
              sub={`${totals.complaints ?? 0} complaints · avg ★ ${totals.avg_rating ?? "—"}`}
              icon={MessageSquare} tone="amber" testid="rollup-kpi-feedback" />
            <KpiCard label="Attendance" value={totals.attendance_pct != null ? `${totals.attendance_pct}%` : "—"}
              sub={`${totals.late_pct != null ? `${totals.late_pct}% late` : "no records"} · ${totals.attendance_records ?? 0} clock-ins`}
              icon={Fingerprint}
              tone={(totals.late_pct || 0) >= 25 ? "red" : "emerald"} testid="rollup-kpi-attendance" />
            <KpiCard label="Active staff" value={totals.staff_count ?? 0}
              sub={`${totals.reports_submitted ?? 0} periodic report(s) in range`}
              icon={Users} tone="slate" testid="rollup-kpi-staff" />
          </div>

          {/* Sales chart */}
          {hasSales && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6" data-testid="rollup-sales-chart">
              <div className="text-[10px] uppercase tracking-[0.14em] text-gray-500 font-semibold mb-3">
                Sales — actual vs target by branch
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1e6)}M`} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Target" fill="#94A3B8" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Actual" fill="#065F46" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Per-branch table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto" data-testid="rollup-table">
            <table className="w-full text-sm min-w-[1080px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-3">Branch</th>
                  <th className="px-4 py-3 text-right">Sales target</th>
                  <th className="px-4 py-3 text-right">Sales actual</th>
                  <th className="px-4 py-3">Variance</th>
                  <th className="px-4 py-3 text-right">Expenditure</th>
                  <th className="px-4 py-3 text-center">Issues (open)</th>
                  <th className="px-4 py-3 text-center">Critical</th>
                  <th className="px-4 py-3 text-center">Complaints</th>
                  <th className="px-4 py-3 text-center">Avg ★</th>
                  <th className="px-4 py-3 text-center">Staff</th>
                  <th className="px-4 py-3 text-center">Attendance</th>
                  <th className="px-4 py-3 text-center">Late %</th>
                  <th className="px-4 py-3 text-center">Reports</th>
                </tr>
              </thead>
              <tbody>
                {branches.map((b) => (
                  <tr key={b.branch} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60"
                    data-testid={`rollup-row-${b.branch.toLowerCase().replaceAll(" ", "-")}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{b.branch}</div>
                      <div className="text-[11px] text-gray-500">{b.manager || b.location || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{fmtMoney(b.sales_target)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmtMoney(b.sales_actual)}</td>
                    <td className="px-4 py-3"><VarianceBadge pct={b.sales_variance_pct} /></td>
                    <td className="px-4 py-3 text-right text-gray-600">{fmtMoney(b.expenditure)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-semibold">{b.issues_open}</span>
                      <span className="text-gray-400"> / {b.issues_total}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {b.issues_critical > 0
                        ? <Badge tone="red">{b.issues_critical}</Badge>
                        : <span className="text-gray-400">0</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {b.complaints > 0
                        ? <Badge tone="amber">{b.complaints}</Badge>
                        : <span className="text-gray-400">0</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">{b.avg_rating ?? "—"}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{b.staff_count}</td>
                    <td className="px-4 py-3 text-center">
                      {b.attendance_pct != null ? (
                        <span className={`font-semibold ${b.attendance_pct >= 80 ? "text-emerald-700" : "text-amber-700"}`}>
                          {b.attendance_pct}%
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                      {b.attendance_records > 0 && (
                        <div className="text-[10px] text-gray-400">{b.attendance_records} clock-ins</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {b.late_pct != null ? (
                        b.late_pct >= 25
                          ? <Badge tone="red">{b.late_pct}%</Badge>
                          : <span className="text-gray-700">{b.late_pct}%</span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">{b.reports_submitted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {branches.length === 0 && (
              <EmptyState title="No branches found" hint="Add branches under Administration → Branches." />
            )}
          </div>
        </>
      )}
    </div>
  );
}
